import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import './ModuleAdmin.css';

const emptyInstruction = {
  name: '',
  description: '',
  order: 0
};

const TrekInstructionForm = () => {
  const { trekId, instructionId } = useParams();
  const isEdit = instructionId && instructionId !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: trekRes } = useQuery(
    ['trek', trekId],
    () => trekAPI.get(trekId),
    { enabled: !!trekId }
  );
  const trek = trekRes?.data?.data;

  const { data: instructionRes } = useQuery(
    ['trek-instructions', trekId],
    () => trekAPI.listInstructions(trekId),
    { enabled: isEdit && !!trekId }
  );
  const existingInstruction = isEdit
    ? (instructionRes?.data?.data || []).find((i) => String(i.id) === String(instructionId))
    : null;

  const [form, setForm] = useState(emptyInstruction);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (existingInstruction) {
      setForm({
        name: existingInstruction.name || '',
        description: existingInstruction.description || '',
        order: existingInstruction.order != null ? existingInstruction.order : 0
      });
    }
  }, [existingInstruction]);

  const mutation = useMutation(
    (payload) =>
      isEdit
        ? trekAPI.updateInstruction(trekId, instructionId, payload)
        : trekAPI.createInstruction(trekId, payload),
    {
      onSuccess: (response) => {
        if (!isMountedRef.current) return;
        queryClient.invalidateQueries(['trek-instructions', trekId]);
        if (!isEdit) {
          const createdId = response?.data?.data?.id;
          if (createdId) navigate(`/treks/${trekId}/instructions/${createdId}`, { replace: true });
        }
        showToast({
          message: isEdit ? 'Instruction updated successfully.' : 'Instruction created successfully.',
          severity: 'success'
        });
      }
    }
  );

  const buildPayload = () => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    order: form.order ? Number(form.order) : 0
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast({
        message: 'Name is required.',
        severity: 'warning'
      });
      return;
    }
    const payload = buildPayload();
    mutation.mutate(payload);
  };

  return (
    <div className="module-page">
      <AdminBackLink to="/treks" state={{ openTrekId: trekId }} label="Back to treks" />
      <div className="module-header">
        <div>
          <h1 className="module-title">{isEdit ? 'Edit Exclusions' : 'Add Exclusions'}</h1>
          <p className="module-subtitle">
            {trek ? trek.name : 'Add instruction details for trek'}
          </p>
        </div>
      </div>

      <div className="module-card">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea
                rows="8"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <label>Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
              />
            </div>
          </div>

          <div className="legacy-main-actions">
            <button className="module-btn" type="button" disabled={mutation.isLoading} onClick={handleSubmit}>
              {isEdit ? 'Update Instructions' : 'Save'}
            </button>
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => navigate('/treks', { state: { openTrekId: trekId } })}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrekInstructionForm;
